import { useOutletContext, useParams } from 'react-router-dom'

import { Section } from '@factorialco/deprecated-design-system'
import { ProjectManagementProjectDetailsPageSettingsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { UpsellFixedTimeBudgetSettings } from 'modules/project_management/components/upsells/UpsellFixedTimeBudgetSettings'

import { BasicInformationSection } from './BasicInformationSection'
import { FixedBudgetSection } from './BudgetSection/fixedTimeBudget'
import { FinancialDetailsSection } from './FinancialDetailsSection'
import { RolesSection } from './RolesSection'
import { StatusSection } from './StatusSection'

const SettingsPage = () => {
  const { id } = useParams()
  const projectId = Number(id)
  const { hasBudgetFeature, hasCostsFeature, hasClientsFeature, hasSubprojectsFeature } =
    useOutletContext<{
      hasBudgetFeature: boolean
      hasCostsFeature: boolean
      hasClientsFeature: boolean
      hasSubprojectsFeature: boolean
    }>()
  const { company, employee } = useCurrentSession()

  const { data, loading, error } = useQuery(
    ProjectManagementProjectDetailsPageSettingsPageDocument,
    {
      variables: {
        companyId: company.id,
        projectId: projectId,
        projectBudgetIsEnabled: hasBudgetFeature,
        projectCostsIsEnabled: hasCostsFeature,
      },
    }
  )

  const project = data?.projectManagement.project
  const canAssignEditor = data?.projectManagement.project?._policies?.assignEditor.can ?? false

  return (
    <ResponsivePage.Body
      showHeader={false}
      loading={loading}
      error={error}
      variant="wide"
      authorized={!!project && canAssignEditor}
    >
      {project && (
        <>
          <StatusSection project={project} />
          <BasicInformationSection
            project={project}
            companyId={company.id}
            hasClientsFeature={hasClientsFeature}
            data={data}
            hasSubprojectsFeature={hasSubprojectsFeature}
          />
          <RolesSection project={project} currentEmployeeId={Number(employee?.id)} />
          {!hasSubprojectsFeature && hasCostsFeature && (
            <FinancialDetailsSection data={data} project={project} />
          )}
          {!hasBudgetFeature ? (
            <Section
              illustration="money"
              title={i18n.t('project_management.planning.title')}
              description={i18n.t('project_management.planning.description')}
              paddingX="s0"
            >
              <UpsellFixedTimeBudgetSettings />
            </Section>
          ) : (
            <FixedBudgetSection project={project} hasSubprojectsFeature={hasSubprojectsFeature} />
          )}
        </>
      )}
    </ResponsivePage.Body>
  )
}

export default SettingsPage
